import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spin, message, Card } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { judgeTaskExecutionApi } from '../../services/api';
import ResultStatsDisplay from '../../components/ResultStatsDisplay';

/**
 * 结果统计页面
 * 展示评测任务执行历史的结果统计数据
 */
function ResultStats() {
  const { executionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskName = searchParams.get('taskName') || '';
  
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(null);
  const [executionInfo, setExecutionInfo] = useState(null);

  useEffect(() => {
    if (executionId) {
      fetchExecutionDetail();
    }
  }, [executionId]);

  const fetchExecutionDetail = async () => {
    setLoading(true);
    try {
      const response = await judgeTaskExecutionApi.getDetail(executionId);
      setExecutionInfo(response);
      
      // 解析 aleResult 字段
      if (response?.aleResult) {
        try {
          const parsedData = typeof response.aleResult === 'string'
            ? JSON.parse(response.aleResult)
            : response.aleResult;
          setStatsData(parsedData);
        } catch (parseError) {
          console.error('解析结果统计数据失败:', parseError);
          message.error('结果统计数据格式错误');
        }
      }
    } catch (error) {
      console.error('获取执行历史详情失败:', error);
      message.error('获取执行历史详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // 由于页面是通过 window.open 在新标签页打开的，navigate(-1) 没有历史记录
    // 尝试关闭当前标签页，如果无法关闭则跳转到任务执行历史页面
    if (window.opener) {
      window.close();
    } else {
      navigate('/judge-task');
    }
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE5E5 0%, #FFF0E5 14.28%, #FFFBE5 28.56%, #F0FFE5 42.84%, #E5F5FF 57.12%, #EBE5FF 71.4%, #F5E5FF 85.68%, #FFE5F5 100%)'
      }}
    >
      {/* 顶部导航栏 */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 600,
              color: '#262626'
            }}
          >
            结果统计
          </h2>
          {taskName && (
            <span style={{ color: '#666', fontSize: '14px' }}>
              - {taskName}
            </span>
          )}
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ padding: '24px 32px' }}>
        <div 
          style={{
            background: 'rgba(255, 255, 255, 0.98)',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            minHeight: 'calc(100vh - 140px)'
          }}
        >
          {loading ? (
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '400px'
              }}
            >
              <Spin size="large" tip="加载中..." />
            </div>
          ) : (
            <>
              {/* 执行信息摘要 */}
              {executionInfo && (
                <Card 
                  className="mb-6"
                  style={{ 
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div style={{
                      background: 'linear-gradient(135deg, #FFE5E5 0%, #FFF0E5 100%)',
                      padding: '16px',
                      borderRadius: '10px'
                    }}>
                      <div className="text-gray-500 text-sm">执行ID</div>
                      <div className="font-semibold text-gray-800">{executionInfo.id}</div>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #FFFBE5 0%, #F0FFE5 100%)',
                      padding: '16px',
                      borderRadius: '10px'
                    }}>
                      <div className="text-gray-500 text-sm">任务名称</div>
                      <div className="font-semibold text-gray-800">{executionInfo.taskName || taskName || '-'}</div>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #E5F5FF 0%, #EBE5FF 100%)',
                      padding: '16px',
                      borderRadius: '10px'
                    }}>
                      <div className="text-gray-500 text-sm">文件准确率</div>
                      <div style={{
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>{executionInfo.filePassRate || '-'}</div>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #F5E5FF 0%, #FFE5F5 100%)',
                      padding: '16px',
                      borderRadius: '10px'
                    }}>
                      <div className="text-gray-500 text-sm">字段准确率</div>
                      <div style={{
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>{executionInfo.fieldPassRate || '-'}</div>
                    </div>
                  </div>
                </Card>
              )}

              {/* 统计数据展示 */}
              <ResultStatsDisplay data={statsData} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResultStats;
